import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, Card, Select, Spin,
} from 'antd';

import { formatValidFaxNumber } from '../../../global/validation';
import { faxNumberOwner, faxNumberValue } from '../walkthroughSteps';

const { Option } = Select;

/**
 * Workspace setup: the user's departments (read-only) and their default sending
 * fax number.
 *
 * Presentational. The container owns the profile fetch, the refresh triggered by
 * adding a number, and the caller_id PATCH; this decides only what to show for a
 * given (loading, failed, empty, populated) state.
 */
const WorkspaceStep = ({
  departments,
  faxNumbers,
  selectedFaxNumber,
  loaded,
  refreshing,
  loadFailed,
  isTrialUser,
  isAdmin,
  canProvisionDefaultOrganizationFax,
  onRetry,
  onSelectFaxNumber,
  onAddFaxNumber,
}) => {
  if (!loaded || refreshing || loadFailed) {
    return (
      <div className="walkthrough-compact-step">
        <h2>Set up your workspace</h2>
        {loadFailed ? (
          <>
            <p className="walkthrough-inline-error">We could not load your workspace settings.</p>
            <Button type="primary" onClick={onRetry}>Retry</Button>
          </>
        ) : (
          <>
            <Spin />
            <p>Loading your workspace settings...</p>
          </>
        )}
      </div>
    );
  }

  const availableFaxNumbers = faxNumbers || [];
  return (
    <div className="walkthrough-compact-step">
      <h2>Set up your workspace</h2>
      <p>Your departments are managed by your organization. Choose your default sending fax number.</p>
      <Card size="small" className="walkthrough-workspace-card">
        <div className="walkthrough-workspace-row">
          <strong>Departments</strong>
          <span>{departments.length ? departments.map((department) => department.name).join(', ') : 'No departments assigned'}</span>
        </div>
        <div className="walkthrough-workspace-row">
          <strong>Default sending fax number</strong>
          {availableFaxNumbers.length ? (
            <Select value={selectedFaxNumber || undefined} onChange={onSelectFaxNumber} style={{ width: '100%', maxWidth: 340 }}>
              {availableFaxNumbers.map((faxNumber) => {
                const number = faxNumberValue(faxNumber);
                const owner = faxNumberOwner(faxNumber);
                return <Option key={number} value={number}>{owner ? `${formatValidFaxNumber(number)} - ${owner}` : formatValidFaxNumber(number)}</Option>;
              })}
            </Select>
          ) : (
            <span>No fax numbers are available yet.</span>
          )}
        </div>
        {availableFaxNumbers.length > 0 && (
          <p className="walkthrough-help-text">
            This number is preselected for outbound faxes. You can choose a different number each time you send.
          </p>
        )}
        {/* The offer is withheld when buying a number would not resolve the empty
            state: this list is scoped to the DEFAULT organization, but the add-fax
            modal provisions for the session organization (User#organization =
            current_signed_in_organization || default_organization). For a
            multi-org admin signed into a non-default org those differ, so the
            purchase would be real and billable and the step would still read
            "No fax numbers are available yet." See
            User#can_provision_default_organization_fax. */}
        {!availableFaxNumbers.length && isTrialUser && isAdmin && (
          canProvisionDefaultOrganizationFax
            ? <Button type="primary" onClick={onAddFaxNumber}>Get a fax number</Button>
            : (
              <p className="walkthrough-help-text">
                Switch to your default organization to add a fax number, or ask your administrator to add one for you.
              </p>
            )
        )}
      </Card>
    </div>
  );
};

WorkspaceStep.defaultProps = {
  departments: [],
  faxNumbers: [],
  selectedFaxNumber: null,
  loaded: false,
  refreshing: false,
  loadFailed: false,
  isTrialUser: false,
  isAdmin: false,
  canProvisionDefaultOrganizationFax: true,
};

WorkspaceStep.propTypes = {
  departments: PropTypes.array,
  faxNumbers: PropTypes.array,
  selectedFaxNumber: PropTypes.string,
  loaded: PropTypes.bool,
  refreshing: PropTypes.bool,
  loadFailed: PropTypes.bool,
  isTrialUser: PropTypes.bool,
  isAdmin: PropTypes.bool,
  canProvisionDefaultOrganizationFax: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  onSelectFaxNumber: PropTypes.func.isRequired,
  onAddFaxNumber: PropTypes.func.isRequired,
};

export default WorkspaceStep;
